/**
 * DD Obligations V3 - Regulatory Execution Registry
 * 
 * Enterprise-grade regulatory execution control.
 * Purpose: What are we legally required to do? Which obligations are triggered?
 * What state are they in? What evidence is attached? Who owns them? What is overdue?
 * 
 * Supports both:
 * - DD Obligations (CSDDD Due Diligence) - framework=CSDDD
 * - Disclosure Obligations (ESRS/CSRD) - framework=ESRS
 * 
 * Architecture:
 * - Defensibility Flywheel (horizontal process layer)
 * - Snapshot Context Bar
 * - Page Header + Filters + Bulk Actions
 * - Obligation Registry Grid
 * - Obligation Detail Panel (Right Drawer)
 * - Financial Impact Modeling
 * - AI Evidence Gap Detection
 * 
 * This is NOT task management. This is regulatory execution control.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Target, Shield, Activity, ChevronRight, ChevronDown, ArrowRight, X,
  FileText, Clock, CheckCircle2, XCircle, AlertCircle, AlertTriangle,
  Users, Building2, Calendar, Scale, Filter, Search, RefreshCw, Download,
  Lock, Unlock, Play, Pause, MoreVertical, ExternalLink, Link2, History,
  Edit3, UserPlus, CalendarClock, Zap, Gavel, Upload, MapPin, ClipboardCheck,
  TrendingUp, TrendingDown, Eye, Radio, Hash, Package, Layers, BarChart3,
  DollarSign, Brain, Sparkles, CircleDollarSign
} from 'lucide-react';
import { useEventRouter } from '../hooks/useEventRouter';
import { can, getPrimaryRole, getSnapshotLockState, getSession } from '../lib/permissions';
import { PermissionGate, SnapshotLockBanner } from '../components/PermissionGate';
import DefensibilityFlywheel from '../components/DefensibilityFlywheel';
import { apiFetch } from '../services/api_service';


const API_URL = process.env.REACT_APP_BACKEND_URL;

// Framework configuration for page display
const FRAMEWORK_MODES = {
  CSDDD: {
    title: 'DD Obligations',
    subtitle: 'CSDDD Execution Registry • Regulatory execution control',
    apiFramework: null,  // Use backend default (CSDDD-family)
    badge: 'CSDDD'
  },
  ESRS: {
    title: 'Disclosure Obligations',
    subtitle: 'ESRS/CSRD Reporting • Sustainability disclosure control',
    apiFramework: 'ESRS',
    badge: 'CSRD'
  },
  ALL: {
    title: 'All Obligations',
    subtitle: 'Combined Registry • All frameworks',
    apiFramework: 'ALL',
    badge: 'ALL'
  }
};

// =============================================================================
// CONSTANTS
// =============================================================================

const STATUS_CONFIG = {
  identified: { label: 'Identified', color: 'slate', icon: Target, next: 'assigned', order: 0 },
  assigned: { label: 'Assigned', color: 'blue', icon: UserPlus, next: 'in_progress', order: 1 },
  in_progress: { label: 'In Progress', color: 'amber', icon: Play, next: 'evidence_attached', order: 2 },
  evidence_attached: { label: 'Evidence Attached', color: 'purple', icon: FileText, next: 'validated', order: 3 },
  validated: { label: 'Validated', color: 'emerald', icon: CheckCircle2, next: 'locked', order: 4 },
  locked: { label: 'Locked', color: 'cyan', icon: Lock, next: 'auditable', order: 5 },
  auditable: { label: 'Auditable', color: 'emerald', icon: Shield, next: null, order: 6 },
  breached: { label: 'Breached', color: 'red', icon: XCircle, next: null, order: -1 }
};

const PRIORITY_CONFIG = {
  critical: { color: 'red', label: 'Critical', weight: 4 },
  high: { color: 'orange', label: 'High', weight: 3 },
  medium: { color: 'amber', label: 'Medium', weight: 2 },
  low: { color: 'emerald', label: 'Low', weight: 1 }
};

const FRAMEWORK_CONFIG = {
  CSDDD: { color: 'emerald', label: 'CSDDD' },
  ESRS: { color: 'blue', label: 'ESRS' },
  GRI: { color: 'purple', label: 'GRI' },
  TCFD: { color: 'cyan', label: 'TCFD' },
  EU_TAXONOMY: { color: 'amber', label: 'EU Taxonomy' }
};

const STATUS_ORDER = ['identified', 'assigned', 'in_progress', 'evidence_attached', 'validated', 'locked', 'auditable'];

const ESCALATION_LEVELS = {
  0: { label: 'Healthy', color: 'emerald', days: 30 },
  1: { label: 'Warning', color: 'amber', days: 30, notifyOwner: true },
  2: { label: 'Risk', color: 'orange', days: 15, notifyCompliance: true },
  3: { label: 'Critical', color: 'red', days: 7, notifyLegal: true },
  4: { label: 'Breached', color: 'red', days: 0, notifyBoard: true }
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const calculateEscalationLevel = (dueDate, status) => {
  if (!dueDate || ['locked', 'auditable'].includes(status)) return 0;
  const days = Math.ceil((new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24));
  if (days <= 0) return 4;
  if (days <= 7) return 3;
  if (days <= 15) return 2;
  if (days <= 30) return 1;
  return 0;
};

const getDaysToBreach = (dueDate) => {
  if (!dueDate) return null;
  return Math.ceil((new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24));
};

// =============================================================================
// KPI STRIP COMPONENT
// =============================================================================

const KPIStrip = ({ stats, financialExposure, onKpiClick }) => {
  // Format currency
  const formatEUR = (value) => {
    if (!value) return '€0';
    if (value >= 1000000) return `€${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `€${(value / 1000).toFixed(0)}K`;
    return `€${value.toFixed(0)}`;
  };

  const kpis = [
    { key: 'active', label: 'Active', value: stats.total || 0, color: 'blue', isNumber: true },
    { key: 'overdue', label: 'Overdue', value: stats.overdue || 0, color: 'red', isNumber: true },
    { key: 'breached', label: 'Breached', value: stats.byStatus?.breached || 0, color: 'red', isNumber: true },
    { key: 'exposure', label: 'Financial Exposure', value: financialExposure?.total_exposure_eur || 0, color: 'amber', isEur: true, icon: CircleDollarSign },
    { key: 'locked', label: 'Locked for Audit', value: (stats.byStatus?.locked || 0) + (stats.byStatus?.auditable || 0), color: 'emerald', isNumber: true },
    { key: 'dueThisWeek', label: 'Due This Week', value: stats.dueThisWeek || 0, color: 'orange', isNumber: true }
  ];

  return (
    <div className="grid grid-cols-6 gap-3" data-testid="kpi-strip">
      {kpis.map(kpi => {
        const Icon = kpi.icon;
        return (
          <button
            key={kpi.key}
            onClick={() => onKpiClick?.(kpi.key)}
            className={`p-3 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg hover:border-${kpi.color}-500/50 transition-colors text-left`}
            data-testid={`kpi-${kpi.key}`}
          >
            <div className="flex items-center justify-between">
              <p className="text-xs text-[#6B6B6B] mb-1">{kpi.label}</p>
              {Icon && <Icon className={`w-3.5 h-3.5 text-${kpi.color}-400`} />}
            </div>
            <p className={`text-xl font-bold text-${kpi.color}-400`}>
              {kpi.isEur ? formatEUR(kpi.value) : kpi.value}
            </p>
          </button>
        );
      })}
    </div>
  );
};

// =============================================================================
// STATE MACHINE TIMELINE COMPONENT
// =============================================================================

const StateMachineTimeline = ({ currentStatus, eventHistory, onTransition, disabled }) => {
  const currentIndex = STATUS_ORDER.indexOf(currentStatus);
  const isBreached = currentStatus === 'breached';

  return (
    <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]" data-testid="state-machine-timeline">
      <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">State Machine</p>
      <div className="flex items-center gap-1 overflow-x-auto py-2">
        {STATUS_ORDER.map((status, idx) => {
          const config = STATUS_CONFIG[status];
          const Icon = config.icon;
          const isPast = idx < currentIndex;
          const isCurrent = idx === currentIndex && !isBreached;
          const isFuture = idx > currentIndex;
          
          // Get event for this status
          const statusEvent = eventHistory?.find(e => e.toStatus === status);
          
          return (
            <React.Fragment key={status}>
              <div 
                className={`relative flex items-center gap-2 px-3 py-2 rounded-lg border transition-all cursor-default
                  ${isCurrent 
                    ? `bg-${config.color}-500/20 border-${config.color}-500/50 text-${config.color}-400 scale-105` 
                    : isPast 
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                      : 'bg-[#0A0A0A] border-[#1F1F1F] text-[#6B6B6B]'
                  }`}
                title={statusEvent ? `${statusEvent.actor} - ${statusEvent.timestamp}` : ''}
              >
                <Icon className="w-4 h-4" />
                <span className="text-xs font-medium whitespace-nowrap">{config.label}</span>
                {isPast && <CheckCircle2 className="w-3 h-3 text-emerald-400 absolute -top-1 -right-1" />}
              </div>
              {idx < STATUS_ORDER.length - 1 && (
                <ArrowRight className={`w-4 h-4 flex-shrink-0 ${isPast ? 'text-emerald-400' : 'text-[#3F3F3F]'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {isBreached && (
        <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
          <XCircle className="w-4 h-4" />
          <span className="text-xs font-medium">BREACHED - Due date has passed</span>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// FLYWHEEL PROGRESS COMPONENT (Obligation-Level)
// =============================================================================

const ObligationFlywheel = ({ obligation }) => {
  const stages = [
    { key: 'upload', label: 'Upload', icon: Upload, progress: obligation?.evidenceCount > 0 ? 1 : 0 },
    { key: 'map', label: 'Map', icon: MapPin, progress: obligation?.legalEntityId ? 1 : 0.5 },
    { key: 'evidence', label: 'Evidence', icon: FileText, progress: obligation?.evidenceCount > 0 ? 0.75 : 0 },
    { key: 'claim', label: 'Claim', icon: ClipboardCheck, progress: 0 },
    { key: 'audit', label: 'Audit', icon: Shield, progress: ['locked', 'auditable'].includes(obligation?.status) ? 1 : 0 }
  ];

  return (
    <div className="p-3 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]" data-testid="obligation-flywheel">
      <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Defensibility Flywheel</p>
      <div className="flex items-center gap-2">
        {stages.map((stage, idx) => {
          const Icon = stage.icon;
          const pct = Math.round(stage.progress * 100);
          const color = pct >= 100 ? 'emerald' : pct >= 50 ? 'amber' : 'red';
          
          return (
            <React.Fragment key={stage.key}>
              <div className="flex flex-col items-center gap-1">
                <div className={`p-2 rounded-lg bg-${color}-500/10 border border-${color}-500/30`}>
                  <Icon className={`w-4 h-4 text-${color}-400`} />
                </div>
                <span className="text-[10px] text-[#6B6B6B]">{stage.label}</span>
                <span className={`text-xs font-bold text-${color}-400`}>{pct}%</span>
              </div>
              {idx < stages.length - 1 && (
                <ArrowRight className="w-3 h-3 text-[#3F3F3F]" />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// =============================================================================
// ADVANCED FILTER BAR COMPONENT
// =============================================================================

const AdvancedFilterBar = ({ filters, onFilterChange, stats }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg p-4" data-testid="filter-bar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Filter className="w-4 h-4 text-[#6B6B6B]" />
          <span className="text-sm text-white font-medium">Filters</span>
          {Object.values(filters).filter(v => v).length > 0 && (
            <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded">
              {Object.values(filters).filter(v => v).length} active
            </span>
          )}
        </div>
        <button 
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-[#6B6B6B] hover:text-white"
        >
          {expanded ? 'Collapse' : 'Show Advanced'}
        </button>
      </div>

      {/* Primary Filters Row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input 
            type="text"
            placeholder="Search by ID, article, description..."
            className="w-full pl-9 pr-4 py-2 bg-[#0F0F0F] border border-[#1F1F1F] rounded text-sm text-white placeholder-[#4F4F4F] focus:border-emerald-500/50 focus:outline-none"
            value={filters.search || ''}
            onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
            data-testid="search-input"
          />
        </div>
        
        <select 
          className="px-3 py-2 bg-[#0F0F0F] border border-[#1F1F1F] rounded text-sm text-[#A0A0A0]"
          value={filters.status || ''}
          onChange={(e) => onFilterChange({ ...filters, status: e.target.value })}
          data-testid="status-filter"
        >
          <option value="">All Status</option>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
        
        <select 
          className="px-3 py-2 bg-[#0F0F0F] border border-[#1F1F1F] rounded text-sm text-[#A0A0A0]"
          value={filters.framework || ''}
          onChange={(e) => onFilterChange({ ...filters, framework: e.target.value })}
          data-testid="framework-filter"
        >
          <option value="">All Frameworks</option>
          {Object.entries(FRAMEWORK_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
        
        <select 
          className="px-3 py-2 bg-[#0F0F0F] border border-[#1F1F1F] rounded text-sm text-[#A0A0A0]"
          value={filters.priority || ''}
          onChange={(e) => onFilterChange({ ...filters, priority: e.target.value })}
          data-testid="priority-filter"
        >
          <option value="">All Priority</option>
          {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>

        <button 
          onClick={() => onFilterChange({})}
          className="px-3 py-2 text-xs text-[#6B6B6B] hover:text-white"
        >
          Clear All
        </button>
      </div>

      {/* Advanced Filters (Expandable) */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-[#1F1F1F] grid grid-cols-4 gap-3">
          <select 
            className="px-3 py-2 bg-[#0F0F0F] border border-[#1F1F1F] rounded text-sm text-[#A0A0A0]"
            value={filters.owner || ''}
            onChange={(e) => onFilterChange({ ...filters, owner: e.target.value })}
          >
            <option value="">All Owners</option>
            <option value="COMPLIANCE_OWNER">Compliance Owner</option>
            <option value="LEGAL_COUNSEL">Legal Counsel</option>
            <option value="SUSTAINABILITY">Sustainability</option>
          </select>
          
          <select 
            className="px-3 py-2 bg-[#0F0F0F] border border-[#1F1F1F] rounded text-sm text-[#A0A0A0]"
            value={filters.escalation || ''}
            onChange={(e) => onFilterChange({ ...filters, escalation: e.target.value })}
          >
            <option value="">All Escalation</option>
            <option value="3">Critical (≤7 days)</option>
            <option value="2">Risk (≤15 days)</option>
            <option value="1">Warning (≤30 days)</option>
          </select>
          
          <select 
            className="px-3 py-2 bg-[#0F0F0F] border border-[#1F1F1F] rounded text-sm text-[#A0A0A0]"
            value={filters.evidenceCoverage || ''}
            onChange={(e) => onFilterChange({ ...filters, evidenceCoverage: e.target.value })}
          >
            <option value="">Any Evidence</option>
            <option value="missing">Missing Evidence</option>
            <option value="partial">Partial Coverage</option>
            <option value="complete">Complete</option>
          </select>
          
          <select 
            className="px-3 py-2 bg-[#0F0F0F] border border-[#1F1F1F] rounded text-sm text-[#A0A0A0]"
            value={filters.auditStatus || ''}
            onChange={(e) => onFilterChange({ ...filters, auditStatus: e.target.value })}
          >
            <option value="">Any Audit Status</option>
            <option value="locked">Locked for Audit</option>
            <option value="ready">Ready to Lock</option>
            <option value="notReady">Not Ready</option>
          </select>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// OBLIGATION REGISTRY GRID COMPONENT
// =============================================================================

const ObligationRegistryGrid = ({ 
  obligations, 
  loading, 
  onRowClick, 
  selectedIds, 
  onSelectChange,
  sortField,
  sortDir,
  onSort 
}) => {
  const columns = [
    { key: 'select', label: '', width: 'w-[3%]', sortable: false },
    { key: 'id', label: 'Obligation ID', width: 'w-[9%]', sortable: true },
    { key: 'articleRef', label: 'Article', width: 'w-[7%]', sortable: true },
    { key: 'legalEntityName', label: 'Entity', width: 'w-[9%]', sortable: true },
    { key: 'status', label: 'Status', width: 'w-[9%]', sortable: true },
    { key: 'ownerRole', label: 'Owner', width: 'w-[9%]', sortable: true },
    { key: 'dueDate', label: 'Due Date', width: 'w-[8%]', sortable: true },
    { key: 'daysToDue', label: 'Days', width: 'w-[5%]', sortable: true },
    { key: 'evidenceCount', label: 'Evidence', width: 'w-[6%]', sortable: true },
    { key: 'exposure', label: 'Exposure €', width: 'w-[10%]', sortable: true },
    { key: 'priority', label: 'Priority', width: 'w-[7%]', sortable: true },
    { key: 'escalation', label: 'Escalation', width: 'w-[7%]', sortable: false },
    { key: 'actions', label: '', width: 'w-[4%]', sortable: false }
  ];

  // Format currency for exposure column
  const formatExposure = (value) => {
    if (!value) return '-';
    if (value >= 1000000) return `€${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `€${Math.round(value / 1000)}K`;
    return `€${value}`;
  };

  return (
    <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg overflow-hidden" data-testid="obligation-registry">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#1F1F1F] bg-[#0F0F0F]">
              {columns.map(col => (
                <th 
                  key={col.key}
                  className={`px-3 py-3 text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium ${col.width} ${col.sortable ? 'cursor-pointer hover:text-emerald-400' : ''}`}
                  onClick={() => col.sortable && onSort?.(col.key)}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    {col.sortable && sortField === col.key && (
                      <span className="text-emerald-400">{sortDir === 'desc' ? '↓' : '↑'}</span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-[#6B6B6B]">
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Loading obligations...
                </td>
              </tr>
            ) : obligations.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-[#6B6B6B]">
                  No obligations match the current filters
                </td>
              </tr>
            ) : obligations.map(obl => {
              const statusCfg = STATUS_CONFIG[obl.status] || STATUS_CONFIG.identified;
              const priorityCfg = PRIORITY_CONFIG[obl.priority] || PRIORITY_CONFIG.medium;
              const escalation = calculateEscalationLevel(obl.dueDate, obl.status);
              const escalationCfg = ESCALATION_LEVELS[escalation];
              const days = getDaysToBreach(obl.dueDate);
              const isSelected = selectedIds?.includes(obl.id);

              return (
                <tr 
                  key={obl.id}
                  className={`border-b border-[#1F1F1F] hover:bg-[#0F0F0F] cursor-pointer transition-colors
                    ${escalation >= 3 ? 'bg-red-500/5' : ''}
                    ${isSelected ? 'bg-emerald-500/10' : ''}`}
                  onClick={() => onRowClick?.(obl)}
                  data-testid={`obligation-row-${obl.id}`}
                >
                  <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                    <input 
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => onSelectChange?.(obl.id, e.target.checked)}
                      className="w-4 h-4 rounded border-[#3F3F3F] bg-[#0F0F0F]"
                    />
                  </td>
                  <td className="px-3 py-3">
                    <span className="font-mono text-xs text-white">{obl.id?.slice(0, 12)}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs text-emerald-400">{obl.articleRef || obl.framework}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs text-[#A0A0A0]">{obl.legalEntityName || 'N/A'}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded bg-${statusCfg.color}-500/20 text-${statusCfg.color}-400`}>
                      {statusCfg.label}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs text-[#A0A0A0]">{obl.ownerRole || 'Unassigned'}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs text-[#A0A0A0]">
                      {obl.dueDate ? new Date(obl.dueDate).toLocaleDateString() : 'N/A'}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-xs font-bold ${
                      days !== null && days <= 0 ? 'text-red-400' :
                      days !== null && days <= 7 ? 'text-orange-400' :
                      days !== null && days <= 15 ? 'text-amber-400' :
                      'text-[#A0A0A0]'
                    }`}>
                      {days !== null ? (days <= 0 ? 'OVERDUE' : days) : 'N/A'}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-xs ${obl.evidenceCount > 0 ? 'text-emerald-400' : 'text-[#6B6B6B]'}`}>
                      {obl.evidenceCount || 0}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col">
                      <span className={`text-xs font-bold ${
                        obl.exposureContribution?.expectedLoss > 500000 ? 'text-red-400' :
                        obl.exposureContribution?.expectedLoss > 100000 ? 'text-amber-400' :
                        'text-emerald-400'
                      }`}>
                        {formatExposure(obl.exposureContribution?.expectedLoss)}
                      </span>
                      {obl.exposureContribution?.p90 && (
                        <span className="text-[10px] text-[#6B6B6B]">
                          P90: {formatExposure(obl.exposureContribution.p90)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded bg-${priorityCfg.color}-500/20 text-${priorityCfg.color}-400`}>
                      {priorityCfg.label}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    {escalation > 0 && (
                      <span className={`px-2 py-0.5 text-xs rounded bg-${escalationCfg.color}-500/20 text-${escalationCfg.color}-400`}>
                        L{escalation}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <button className="p-1 hover:bg-[#1F1F1F] rounded">
                      <MoreVertical className="w-4 h-4 text-[#6B6B6B]" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// =============================================================================
// OBLIGATION DETAIL PANEL COMPONENT
// =============================================================================

const ObligationDetailPanel = ({ 
  obligation, 
  onClose, 
  onTransition, 
  isSnapshotLocked,
  permissions 
}) => {
  const [activeSection, setActiveSection] = useState('overview');
  const [aiGapAnalysis, setAiGapAnalysis] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [obligationLiability, setObligationLiability] = useState(null);
  const statusCfg = STATUS_CONFIG[obligation?.status] || STATUS_CONFIG.identified;
  const escalation = calculateEscalationLevel(obligation?.dueDate, obligation?.status);
  const escalationCfg = ESCALATION_LEVELS[escalation];
  const days = getDaysToBreach(obligation?.dueDate);

  // Fetch AI gap analysis when AI Insights tab is selected
  useEffect(() => {
    if (activeSection === 'ai_insights' && obligation?.id && !aiGapAnalysis) {
      setLoadingAI(true);
      apiFetch(`/spine/v1/ai-evidence/obligation/${obligation.id}/gaps`)
        .then(data => {
          if (data && !data._error) setAiGapAnalysis(data);
          setLoadingAI(false);
        })
        .catch(err => {
          console.error('Error fetching AI analysis:', err);
          setLoadingAI(false);
        });
    }
  }, [activeSection, obligation?.id, aiGapAnalysis]);

  // Fetch liability when Financial tab is selected
  useEffect(() => {
    if (activeSection === 'financial' && obligation?.id && !obligationLiability) {
      apiFetch(`/spine/v1/financial/obligation/${obligation.id}/liability`)
        .then(data => {
          if (data && !data._error) setObligationLiability(data);
        })
        .catch(err => console.error('Error fetching liability:', err));
    }
  }, [activeSection, obligation?.id, obligationLiability]);

  if (!obligation) return null;

  const sections = [
    { key: 'overview', label: 'Overview', icon: Eye },
    { key: 'legal', label: 'Legal Basis', icon: Gavel },
    { key: 'evidence', label: 'Evidence', icon: FileText },
    { key: 'ai_insights', label: 'AI Insights', icon: Sparkles, badge: 'AI' },
    { key: 'financial', label: 'Financial', icon: CircleDollarSign },
    { key: 'audit', label: 'Audit', icon: Shield },
    { key: 'history', label: 'History', icon: History }
  ];

  return (
    <div className="fixed right-0 top-0 h-full w-[45%] bg-[#0A0A0A] border-l border-[#1F1F1F] shadow-2xl z-40 flex flex-col" data-testid="obligation-detail-panel">
      {/* Header */}
      <div className="p-4 border-b border-[#1F1F1F] flex items-center justify-between bg-[#0F0F0F]">
        <div className="flex items-center gap-3">
          <div className={`p-2 bg-${statusCfg.color}-500/10 rounded-lg border border-${statusCfg.color}-500/30`}>
            <Target className={`w-5 h-5 text-${statusCfg.color}-400`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-white font-bold">{obligation.id?.slice(0, 16)}</span>
              <span className={`px-2 py-0.5 text-xs rounded bg-${statusCfg.color}-500/20 text-${statusCfg.color}-400`}>
                {statusCfg.label}
              </span>
              {escalation >= 2 && (
                <span className={`px-2 py-0.5 text-xs rounded bg-${escalationCfg.color}-500/20 text-${escalationCfg.color}-400 animate-pulse`}>
                  {escalationCfg.label}
                </span>
              )}
            </div>
            <p className="text-xs text-[#6B6B6B] mt-0.5">
              {obligation.articleRef || obligation.framework} • {obligation.legalEntityName || 'No Entity'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isSnapshotLocked && (
            <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded border border-blue-500/30">
              <Lock className="w-3 h-3 inline mr-1" />
              Locked
            </span>
          )}
          <button onClick={onClose} className="p-2 hover:bg-[#1F1F1F] rounded">
            <X className="w-5 h-5 text-[#6B6B6B]" />
          </button>
        </div>
      </div>

      {/* State Machine */}
      <div className="px-4 py-3 border-b border-[#1F1F1F]">
        <StateMachineTimeline 
          currentStatus={obligation.status} 
          disabled={isSnapshotLocked}
        />
      </div>

      {/* Flywheel */}
      <div className="px-4 py-3 border-b border-[#1F1F1F]">
        <ObligationFlywheel obligation={obligation} />
      </div>

      {/* Section Tabs */}
      <div className="px-4 py-2 border-b border-[#1F1F1F] flex gap-1 overflow-x-auto">
        {sections.map(sec => {
          const Icon = sec.icon;
          return (
            <button
              key={sec.key}
              onClick={() => setActiveSection(sec.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs whitespace-nowrap transition-colors
                ${activeSection === sec.key 
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                  : 'text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F]'}`}
            >
              <Icon className="w-3 h-3" />
              {sec.label}
              {sec.badge && (
                <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-purple-500/20 text-purple-400 rounded">
                  {sec.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Section Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {activeSection === 'overview' && (
          <>
            {/* SLA & Exposure */}
            <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
              <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">SLA & Exposure</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#6B6B6B]">Owner</p>
                  <p className="text-sm text-white">{obligation.ownerRole || 'Unassigned'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6B6B6B]">Due Date</p>
                  <p className="text-sm text-white">
                    {obligation.dueDate ? new Date(obligation.dueDate).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#6B6B6B]">Days to Breach</p>
                  <p className={`text-sm font-bold ${
                    days !== null && days <= 0 ? 'text-red-400' :
                    days !== null && days <= 7 ? 'text-orange-400' :
                    'text-white'
                  }`}>
                    {days !== null ? (days <= 0 ? `${Math.abs(days)} days overdue` : `${days} days`) : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#6B6B6B]">Escalation Level</p>
                  <p className={`text-sm font-bold text-${escalationCfg.color}-400`}>
                    Level {escalation} - {escalationCfg.label}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F] text-center">
                <p className="text-xs text-[#6B6B6B]">Evidence</p>
                <p className="text-lg font-bold text-white">{obligation.evidenceCount || 0}</p>
              </div>
              <div className="p-3 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F] text-center">
                <p className="text-xs text-[#6B6B6B]">Priority</p>
                <p className={`text-lg font-bold text-${PRIORITY_CONFIG[obligation.priority]?.color || 'amber'}-400`}>
                  {PRIORITY_CONFIG[obligation.priority]?.label || 'Medium'}
                </p>
              </div>
              <div className="p-3 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F] text-center">
                <p className="text-xs text-[#6B6B6B]">Framework</p>
                <p className="text-lg font-bold text-emerald-400">{obligation.framework || 'CSDDD'}</p>
              </div>
            </div>
          </>
        )}

        {activeSection === 'legal' && (
          <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
            <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Legal Basis</p>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-[#6B6B6B]">Framework</p>
                <p className="text-sm text-white">{obligation.framework || 'CSDDD'}</p>
              </div>
              <div>
                <p className="text-xs text-[#6B6B6B]">Article Reference</p>
                <p className="text-sm text-emerald-400">{obligation.articleRef || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-[#6B6B6B]">Template ID</p>
                <p className="text-sm font-mono text-[#A0A0A0]">{obligation.templateId || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-[#6B6B6B]">Mandatory</p>
                <p className="text-sm text-white">Yes</p>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'evidence' && (
          <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
            <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Evidence Checklist</p>
            {obligation.evidenceCount > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-[#0A0A0A] rounded border border-[#1F1F1F]">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-white">HR Audit Report</span>
                  </div>
                  <span className="px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded">Validated</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[#6B6B6B]">No evidence attached yet</p>
            )}
            {!isSnapshotLocked && (
              <button className="mt-3 w-full px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded text-sm hover:bg-emerald-500/20">
                <Upload className="w-4 h-4 inline mr-2" />
                Upload Evidence
              </button>
            )}
          </div>
        )}

        {activeSection === 'ai_insights' && (
          <div className="space-y-4">
            {loadingAI ? (
              <div className="p-8 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F] text-center">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3 text-purple-400" />
                <p className="text-sm text-[#6B6B6B]">Analyzing evidence gaps with AI...</p>
              </div>
            ) : aiGapAnalysis ? (
              <>
                {/* Sufficiency Score */}
                <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Evidence Sufficiency</p>
                    <span className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded">
                      {aiGapAnalysis.ai_model}
                    </span>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {Object.entries(aiGapAnalysis.sufficiency_score || {}).map(([key, value]) => (
                      <div key={key} className="text-center">
                        <p className={`text-lg font-bold ${value >= 70 ? 'text-emerald-400' : value >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                          {Math.round(value)}%
                        </p>
                        <p className="text-[10px] text-[#6B6B6B] capitalize">{key.replace(/_/g, ' ')}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* AI Summary */}
                <div className="p-4 bg-purple-500/5 rounded-lg border border-purple-500/20">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-purple-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-white mb-1">AI Summary</p>
                      <p className="text-xs text-[#A0A0A0]">{aiGapAnalysis.ai_summary}</p>
                    </div>
                  </div>
                </div>

                {/* Evidence Gaps */}
                {aiGapAnalysis.gaps?.length > 0 && (
                  <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
                    <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Detected Gaps</p>
                    <div className="space-y-2">
                      {aiGapAnalysis.gaps.map((gap, idx) => (
                        <div key={idx} className={`p-3 rounded border ${
                          gap.severity === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                          gap.severity === 'high' ? 'bg-orange-500/10 border-orange-500/30' :
                          'bg-amber-500/10 border-amber-500/30'
                        }`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`px-2 py-0.5 text-[10px] rounded uppercase ${
                              gap.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                              gap.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                              'bg-amber-500/20 text-amber-400'
                            }`}>
                              {gap.severity}
                            </span>
                            <span className="text-[10px] text-[#6B6B6B]">{Math.round(gap.confidence * 100)}% confidence</span>
                          </div>
                          <p className="text-xs text-white">{gap.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Suggested Actions */}
                {aiGapAnalysis.suggested_actions?.length > 0 && (
                  <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
                    <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Suggested Actions</p>
                    <div className="space-y-2">
                      {aiGapAnalysis.suggested_actions.map((action, idx) => (
                        <div key={idx} className="p-3 bg-[#0A0A0A] rounded border border-[#1F1F1F]">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`px-2 py-0.5 text-[10px] rounded ${
                              action.priority === 'immediate' ? 'bg-red-500/20 text-red-400' :
                              action.priority === 'soon' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {action.priority}
                            </span>
                            <span className="text-[10px] text-[#6B6B6B]">{action.estimated_effort} effort</span>
                          </div>
                          <p className="text-xs text-white">{action.description}</p>
                          {action.responsible_role && (
                            <p className="text-[10px] text-[#6B6B6B] mt-1">Assign to: {action.responsible_role}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Disclaimer */}
                <p className="text-[10px] text-[#6B6B6B] italic text-center">
                  {aiGapAnalysis.disclaimer}
                </p>
              </>
            ) : (
              <div className="p-8 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F] text-center">
                <Brain className="w-8 h-8 mx-auto mb-3 text-[#3F3F3F]" />
                <p className="text-sm text-[#6B6B6B]">AI analysis not available</p>
              </div>
            )}
          </div>
        )}

        {activeSection === 'financial' && (
          <div className="space-y-4" data-testid="exposure-impact-panel">
            {/* Header */}
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-xs text-amber-400 font-semibold">Financial Impact of This Obligation</p>
            </div>

            {obligationLiability ? (
              <>
                {/* Expected Loss & P90 - Hero Numbers */}
                <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-xs text-[#6B6B6B] mb-1">Expected Loss Contribution</p>
                      <p className="text-2xl font-bold text-amber-400">
                        €{(obligationLiability.liability?.total_liability || 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#6B6B6B] mb-1">P90 Contribution</p>
                      <p className="text-2xl font-bold text-red-400">
                        €{Math.round((obligationLiability.liability?.total_liability || 0) * 2.2).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 text-center">
                    <span className="text-[10px] text-[#6B6B6B]">Confidence: </span>
                    <span className={`text-[10px] font-medium ${
                      (obligationLiability.liability?.confidence || 0.7) >= 0.7 ? 'text-emerald-400' : 'text-amber-400'
                    }`}>
                      {Math.round((obligationLiability.liability?.confidence || 0.7) * 100)}%
                    </span>
                  </div>
                </div>

                {/* Component Breakdown */}
                <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
                  <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Component Breakdown</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-blue-500" />
                        <span className="text-xs text-white">Regulatory Penalty (RPE)</span>
                      </div>
                      <span className="text-xs text-blue-400">€{Math.round((obligationLiability.liability?.base_fine || 0) * 0.4).toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-[#1F1F1F] rounded-full h-1.5">
                      <div className="bg-blue-500 h-1.5 rounded-full" style={{width: '40%'}} />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-purple-500" />
                        <span className="text-xs text-white">Remediation Cost (RCE)</span>
                      </div>
                      <span className="text-xs text-purple-400">€{Math.round((obligationLiability.liability?.severity_adjustment || 0) * 0.5).toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-[#1F1F1F] rounded-full h-1.5">
                      <div className="bg-purple-500 h-1.5 rounded-full" style={{width: '30%'}} />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-red-500" />
                        <span className="text-xs text-white">Civil Liability (CLE)</span>
                      </div>
                      <span className="text-xs text-red-400">€{Math.round((obligationLiability.liability?.escalation_adjustment || 0) + (obligationLiability.liability?.severity_adjustment || 0) * 0.5).toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-[#1F1F1F] rounded-full h-1.5">
                      <div className="bg-red-500 h-1.5 rounded-full" style={{width: '30%'}} />
                    </div>
                  </div>
                </div>

                {/* Exposure Reduction Levers */}
                <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
                  <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Exposure Reduction Levers</p>
                  <div className="space-y-2">
                    {obligation?.evidenceCount === 0 && (
                      <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-white">Upload Evidence Documents</p>
                          <p className="text-[10px] text-emerald-400">↓ €{Math.round((obligationLiability.liability?.total_liability || 0) * 0.35).toLocaleString()} EL</p>
                        </div>
                      </div>
                    )}
                    {obligation?.status !== 'validated' && obligation?.status !== 'locked' && (
                      <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-white">Complete Validation Process</p>
                          <p className="text-[10px] text-emerald-400">↓ €{Math.round((obligationLiability.liability?.total_liability || 0) * 0.25).toLocaleString()} EL</p>
                        </div>
                      </div>
                    )}
                    {obligation?.status === 'breached' && (
                      <div className="p-3 bg-red-500/10 border border-red-500/20 rounded flex items-start gap-2">
                        <Zap className="w-4 h-4 text-red-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-white">Initiate Breach Recovery</p>
                          <p className="text-[10px] text-emerald-400">↓ €{Math.round((obligationLiability.liability?.total_liability || 0) * 0.45).toLocaleString()} EL</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Risk Factors */}
                {obligationLiability.risk_factors?.length > 0 && (
                  <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
                    <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Active Risk Factors</p>
                    <div className="flex flex-wrap gap-2">
                      {obligationLiability.risk_factors.map((factor, idx) => (
                        <span key={idx} className="px-2 py-1 text-xs bg-red-500/10 text-red-400 rounded border border-red-500/20">
                          {factor.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* View Full Breakdown Button */}
                <button className="w-full py-2 bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#A0A0A0] text-xs rounded-lg flex items-center justify-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  View Full Breakdown
                  <ArrowRight className="w-3 h-3" />
                </button>

                {/* Calculation Basis Footer */}
                <p className="text-[10px] text-[#6B6B6B] italic text-center">
                  {obligationLiability.liability?.calculation_basis}
                </p>
              </>
            ) : (
              <div className="p-8 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F] text-center">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3 text-[#6B6B6B]" />
                <p className="text-sm text-[#6B6B6B]">Calculating exposure impact...</p>
              </div>
            )}
          </div>
        )}

        {activeSection === 'history' && (
          <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
            <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Event Timeline</p>
            <div className="space-y-2">
              <div className="flex items-start gap-3 p-2 bg-[#0A0A0A] rounded border border-[#1F1F1F]">
                <div className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5" />
                <div>
                  <p className="text-xs text-white">Created</p>
                  <p className="text-xs text-[#6B6B6B]">{obligation.createdAt || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Action Bar */}
      {!isSnapshotLocked && (
        <div className="p-4 border-t border-[#1F1F1F] bg-[#0F0F0F]">
          <div className="flex items-center justify-between">
            <div className="text-xs text-[#6B6B6B]">
              Next: {statusCfg.next ? STATUS_CONFIG[statusCfg.next]?.label : 'Complete'}
            </div>
            {statusCfg.next && (
              <button 
                onClick={() => onTransition?.(obligation.id, statusCfg.next)}
                className="px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 rounded text-sm font-medium hover:bg-emerald-500/30"
                data-testid="transition-btn"
              >
                Transition to {STATUS_CONFIG[statusCfg.next]?.label}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT: DD OBLIGATIONS V3
// =============================================================================

const DDObligationsV3 = () => {
  // Get framework mode from URL query params
  const [searchParams] = useSearchParams();
  const urlFramework = searchParams.get('framework') || 'CSDDD';
  const frameworkMode = FRAMEWORK_MODES[urlFramework] || FRAMEWORK_MODES.CSDDD;
  
  // Event Router for live updates
  const {
    readModels,
    refreshModel,
    refreshAll,
    mode,
    connectionStatus,
    isConnected,
    eventHistory
  } = useEventRouter({
    groupId: 'grp001',
    boundaryId: 'bnd001',
    period: 'FY2024'
  });

  // Permissions - using direct functions
  const primaryRole = getPrimaryRole();
  const session = getSession();
  const snapshotLockState = getSnapshotLockState();
  const isSnapshotLocked = snapshotLockState?.isLocked || false;

  // Local state
  const [obligations, setObligations] = useState([]);
  const [stats, setStats] = useState({});
  const [financialExposure, setFinancialExposure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [selectedObligation, setSelectedObligation] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortField, setSortField] = useState('dueDate');
  const [sortDir, setSortDir] = useState('asc');
  const [showExportModal, setShowExportModal] = useState(false);

  // Fetch obligations from new read model endpoints
  const fetchObligations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        groupId: 'grp001',
        boundaryId: 'bnd001',
        period: 'FY2024',
        limit: '100',
        offset: '0'
      });
      
      // Add framework from URL mode (unless filter override)
      if (filters.framework) {
        params.append('framework', filters.framework);
      } else if (frameworkMode.apiFramework) {
        params.append('framework', frameworkMode.apiFramework);
      }
      // else: use backend default (CSDDD-family for DD Obligations)
      
      if (filters.status) params.append('status', filters.status);
      
      const url = `${API_URL}/api/spine/v1/csddd/readmodels/dd-obligations/list?${params}`;

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setObligations(data.obligations || []);
      }
    } catch (err) {
      console.error('Error fetching obligations:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, frameworkMode.apiFramework]);

  const fetchStats = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        groupId: 'grp001',
        boundaryId: 'bnd001',
        period: 'FY2024'
      });
      
      if (frameworkMode.apiFramework) {
        params.append('framework', frameworkMode.apiFramework);
      }
      
      const response = await apiFetch(`/spine/v1/csddd/readmodels/dd-obligations/stats?${params}`);
      if (response && !response._error) {
        setStats(response.stats || {});
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [frameworkMode.apiFramework]);

  const fetchFinancialExposure = useCallback(async () => {
    try {
      const response = await apiFetch(`/spine/v1/financial/exposure-summary?groupId=grp001&boundaryId=bnd001&period=FY2024`);
      if (response && !response._error) {
        setFinancialExposure(response);
      }
    } catch (err) {
      console.error('Error fetching financial exposure:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchObligations();
    fetchStats();
    fetchFinancialExposure();
  }, [fetchObligations, fetchStats, fetchFinancialExposure]);

  // Listen for event-driven updates (only for default CSDDD mode)
  // Skip event-driven updates when using URL framework filter to avoid overwriting filtered data
  useEffect(() => {
    // Only apply event-driven updates when in default CSDDD mode (no explicit framework filter)
    if (frameworkMode.apiFramework !== null) {
      return; // Skip - we're using a specific framework filter
    }
    
    const ddListModel = readModels.DD_OBLIGATIONS_LIST;
    const ddStatsModel = readModels.DD_OBLIGATIONS_STATS;
    
    if (ddListModel?.data) {
      setObligations(ddListModel.data.obligations || []);
    }
    if (ddStatsModel?.data) {
      setStats(ddStatsModel.data.stats || {});
    }
  }, [readModels, frameworkMode.apiFramework]);

  // Sort obligations
  const sortedObligations = useMemo(() => {
    return [...obligations].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (!aVal && !bVal) return 0;
      if (!aVal) return 1;
      if (!bVal) return -1;
      
      if (sortField === 'dueDate') {
        const diff = new Date(aVal) - new Date(bVal);
        return sortDir === 'asc' ? diff : -diff;
      }
      if (typeof aVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [obligations, sortField, sortDir]);

  // Filtered obligations
  const filteredObligations = useMemo(() => {
    return sortedObligations.filter(obl => {
      if (filters.search) {
        const search = filters.search.toLowerCase();
        if (!obl.id?.toLowerCase().includes(search) && 
            !obl.articleRef?.toLowerCase().includes(search) &&
            !obl.legalEntityName?.toLowerCase().includes(search)) {
          return false;
        }
      }
      if (filters.status && obl.status !== filters.status) return false;
      if (filters.framework && obl.framework !== filters.framework) return false;
      if (filters.priority && obl.priority !== filters.priority) return false;
      return true;
    });
  }, [sortedObligations, filters]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleSelectChange = (id, checked) => {
    setSelectedIds(prev => 
      checked ? [...prev, id] : prev.filter(i => i !== id)
    );
  };

  const handleKpiClick = (kpiKey) => {
    switch (kpiKey) {
      case 'overdue':
        setFilters({ ...filters, escalation: '4' });
        break;
      case 'breached':
        setFilters({ ...filters, status: 'breached' });
        break;
      case 'locked':
        setFilters({ ...filters, status: 'locked' });
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="dd-obligations-v3">
      {/* Snapshot Lock Banner */}
      {isSnapshotLocked && (
        <SnapshotLockBanner 
          snapshotId={snapshotLockState?.snapshotId}
          lockedBy={snapshotLockState?.lockedBy}
        />
      )}

      <div className="p-6 space-y-4">
        {/* Defensibility Flywheel - Horizontal Process Layer */}
        <DefensibilityFlywheel 
          currentStep={selectedObligation ? 'evidence' : 'map'}
          context="obligations"
          entityId={selectedObligation?.id}
          compact={false}
        />

        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-500/10 rounded-xl border border-indigo-500/30">
              <Target className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-outfit text-2xl font-bold text-white">{frameworkMode.title}</h1>
                <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                  frameworkMode.badge === 'CSRD' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
                  frameworkMode.badge === 'CSDDD' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                  'bg-slate-500/20 text-slate-400 border border-slate-500/30'
                }`}>
                  {frameworkMode.badge}
                </span>
              </div>
              <p className="text-sm text-[#6B6B6B]">{frameworkMode.subtitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Connection Status */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
              isConnected 
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' 
                : 'bg-red-500/10 border-red-500/30 text-red-400'
            }`}>
              <Radio className={`w-3 h-3 ${isConnected ? 'animate-pulse' : ''}`} />
              <span className="text-xs font-medium">{mode}</span>
            </div>

            {/* Refresh */}
            <button 
              onClick={() => { fetchObligations(); fetchStats(); fetchFinancialExposure(); }}
              className="p-2 hover:bg-[#1F1F1F] rounded-lg"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 text-[#6B6B6B]" />
            </button>

            {/* Export */}
            <button 
              onClick={() => setShowExportModal(true)}
              className="px-3 py-2 bg-[#1F1F1F] text-[#A0A0A0] rounded-lg text-sm hover:bg-[#2F2F2F]"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 inline mr-2" />
              Export
            </button>
          </div>
        </div>

        {/* KPI Strip */}
        <KPIStrip stats={stats} financialExposure={financialExposure} onKpiClick={handleKpiClick} />

        {/* Advanced Filter Bar */}
        <AdvancedFilterBar 
          filters={filters} 
          onFilterChange={setFilters}
          stats={stats}
        />

        {/* Bulk Actions (when items selected) */}
        {selectedIds.length > 0 && (
          <div className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <span className="text-sm text-emerald-400">
              {selectedIds.length} obligation(s) selected
            </span>
            <div className="flex-1" />
            <button className="px-3 py-1.5 text-xs bg-[#1F1F1F] text-[#A0A0A0] rounded hover:bg-[#2F2F2F]">
              Assign Owner
            </button>
            <button className="px-3 py-1.5 text-xs bg-[#1F1F1F] text-[#A0A0A0] rounded hover:bg-[#2F2F2F]">
              Escalate
            </button>
            <button 
              onClick={() => setSelectedIds([])}
              className="px-3 py-1.5 text-xs text-[#6B6B6B] hover:text-white"
            >
              Clear
            </button>
          </div>
        )}

        {/* Obligation Registry Grid */}
        <ObligationRegistryGrid 
          obligations={filteredObligations}
          loading={loading}
          onRowClick={setSelectedObligation}
          selectedIds={selectedIds}
          onSelectChange={handleSelectChange}
          sortField={sortField}
          sortDir={sortDir}
          onSort={handleSort}
        />

        {/* Pagination Info */}
        <div className="flex items-center justify-between text-xs text-[#6B6B6B]">
          <span>Showing {filteredObligations.length} of {obligations.length} obligations</span>
          <span>Last updated: {new Date().toLocaleTimeString()}</span>
        </div>
      </div>

      {/* Obligation Detail Panel */}
      {selectedObligation && (
        <ObligationDetailPanel 
          obligation={selectedObligation}
          onClose={() => setSelectedObligation(null)}
          isSnapshotLocked={isSnapshotLocked}
          permissions={{ can, primaryRole }}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal onClose={() => setShowExportModal(false)} />
      )}
    </div>
  );
};

// =============================================================================
// EXPORT MODAL COMPONENT
// =============================================================================

const ExportModal = ({ onClose }) => {
  const [config, setConfig] = useState({
    bundleType: 'full_audit',
    format: 'json',
    includeObligations: true,
    includeEvidence: true,
    includeImpacts: true,
    includeSuppliers: true
  });
  const [exportJob, setExportJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const startExport = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`/spine/v1/export/proof-bundle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          group_id: 'grp001',
          boundary_id: 'bnd001',
          period: 'FY2024',
          bundle_type: config.bundleType,
          format: config.format,
          include_obligations: config.includeObligations,
          include_evidence: config.includeEvidence,
          include_impacts: config.includeImpacts,
          include_suppliers: config.includeSuppliers
        })
      });

      if (response && !response._error) {
        setExportJob(response);
        pollJobStatus(response.job_id);
      }
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const pollJobStatus = async (jobId) => {
    const maxAttempts = 30;
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await apiFetch(`/spine/v1/export/jobs/${jobId}`);
        if (response && !response._error) {
          const job = response;
          setExportJob(job);

          if (job.status === 'completed') {
            setDownloadUrl(job.download_url);
            setLoading(false);
          } else if (job.status === 'failed') {
            setLoading(false);
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, 1000);
          }
        }
      } catch (err) {
        setLoading(false);
      }
    };

    poll();
  };

  const handleDownload = async () => {
    if (!downloadUrl) return;
    try {
      const response = await fetch(`${API_URL}${downloadUrl}`);
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `proof_bundle_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center" data-testid="export-modal">
      <div className="bg-[#0F0F0F] border border-[#1F1F1F] rounded-xl w-[500px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-[#1F1F1F]">
          <div className="flex items-center gap-3">
            <Download className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white">Export Proof Bundle</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1F1F1F] rounded">
            <X className="w-5 h-5 text-[#6B6B6B]" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {!exportJob ? (
            <>
              {/* Bundle Type */}
              <div>
                <label className="block text-xs text-[#6B6B6B] mb-2">Bundle Type</label>
                <select
                  value={config.bundleType}
                  onChange={(e) => setConfig({...config, bundleType: e.target.value})}
                  className="w-full p-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded text-sm text-white"
                >
                  <option value="full_audit">Full Audit Pack</option>
                  <option value="obligations_only">Obligations Only</option>
                  <option value="evidence_pack">Evidence Pack</option>
                  <option value="compliance_snapshot">Compliance Snapshot</option>
                </select>
              </div>

              {/* Format */}
              <div>
                <label className="block text-xs text-[#6B6B6B] mb-2">Format</label>
                <div className="flex gap-2">
                  {['json', 'pdf', 'csv'].map(fmt => (
                    <button
                      key={fmt}
                      onClick={() => setConfig({...config, format: fmt})}
                      className={`px-4 py-2 rounded text-sm ${
                        config.format === fmt 
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                          : 'bg-[#0A0A0A] text-[#6B6B6B] border border-[#1F1F1F]'
                      }`}
                    >
                      {fmt.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Include Options */}
              <div>
                <label className="block text-xs text-[#6B6B6B] mb-2">Include</label>
                <div className="space-y-2">
                  {[
                    { key: 'includeObligations', label: 'Obligations' },
                    { key: 'includeEvidence', label: 'Evidence Documents' },
                    { key: 'includeImpacts', label: 'Risk Impacts' },
                    { key: 'includeSuppliers', label: 'Supplier Data' }
                  ].map(opt => (
                    <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config[opt.key]}
                        onChange={(e) => setConfig({...config, [opt.key]: e.target.checked})}
                        className="accent-emerald-500"
                      />
                      <span className="text-sm text-white">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Tamper-Evident Info */}
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-blue-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-blue-400 font-medium">Tamper-Evident Bundle</p>
                    <p className="text-xs text-[#6B6B6B] mt-1">
                      All exported data is cryptographically hashed with SHA-256. 
                      The hash chain ensures data integrity and auditability.
                    </p>
                  </div>
                </div>
              </div>

              {/* Export Button */}
              <button
                onClick={startExport}
                disabled={loading}
                className="w-full py-3 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 disabled:opacity-50"
              >
                {loading ? 'Generating...' : 'Generate Proof Bundle'}
              </button>
            </>
          ) : (
            <div className="text-center py-6">
              {exportJob.status === 'completed' ? (
                <>
                  <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                  <p className="text-lg font-semibold text-white mb-2">Bundle Ready</p>
                  <p className="text-sm text-[#6B6B6B] mb-4">
                    Your proof bundle has been generated and is ready for download.
                  </p>
                  <button
                    onClick={handleDownload}
                    className="px-6 py-3 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600"
                  >
                    <Download className="w-4 h-4 inline mr-2" />
                    Download Bundle
                  </button>
                </>
              ) : exportJob.status === 'failed' ? (
                <>
                  <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                  <p className="text-lg font-semibold text-white mb-2">Export Failed</p>
                  <p className="text-sm text-red-400">{exportJob.error_message}</p>
                </>
              ) : (
                <>
                  <RefreshCw className="w-12 h-12 text-blue-400 mx-auto mb-3 animate-spin" />
                  <p className="text-lg font-semibold text-white mb-2">Generating Bundle</p>
                  <div className="w-full bg-[#1F1F1F] rounded-full h-2 mt-4">
                    <div 
                      className="bg-emerald-500 h-2 rounded-full transition-all"
                      style={{ width: `${exportJob.progress_pct || 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-[#6B6B6B] mt-2">{exportJob.progress_pct || 0}% complete</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DDObligationsV3;
